import { Link } from "react-router-dom";
import { StatusBadge } from "../Ui";
import { fmtChange, fmtInt, overviewUptime, siteName, sparkPoints, type OverviewEntry } from "../../web-analytics";

const SPARK_W = 240;
const SPARK_H = 44;

/**
 * Seven days of visitors as a 2px line. Explicit width and height: the global
 * `svg { width: 18px }` rule would otherwise squash it to an icon.
 */
function Sparkline({ data }: { data: number[] }) {
  const values = data.length ? data : [0, 0, 0, 0, 0, 0, 0];
  const points = sparkPoints(values, SPARK_W, SPARK_H);
  return (
    <svg className="ws-spark" viewBox={`0 0 ${SPARK_W} ${SPARK_H}`} width="100%" height={SPARK_H} preserveAspectRatio="none"
      role="img" aria-label={`Visitors per day, last 7 days: ${values.join(", ")}`}>
      <polygon points={`0,${SPARK_H} ${points} ${SPARK_W},${SPARK_H}`} className="ws-spark-area" />
      <polyline points={points} className="ws-spark-line" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/**
 * One client site on the agency Websites page, ported from Sonar's SiteCard.
 * The title link's ::after stretches over the whole card, so anywhere on it
 * opens the site's analytics page.
 */
export default function SiteCard({ entry }: { entry: OverviewEntry }) {
  const { site, client } = entry;
  const name = siteName(site);
  const change = fmtChange(entry.visitors_7d, entry.visitors_prev_7d);
  const uptime = overviewUptime(entry);
  const live = entry.live > 0;
  return (
    <article className={`panel ws-card${entry.uptime_state === "down" ? " ws-card-down" : ""}`}>
      <div className="ws-card-head">
        <div className="ws-card-title">
          <span className="ws-card-client">{client.name}</span>
          <h2 title={name}>
            <Link className="ws-card-link" to={`/website?client_id=${client.id}&site=${site.id}`}>{name}</Link>
          </h2>
          {/* Always there, empty when the name IS the domain, so every card's sparkline and figures line up. */}
          <span className="ws-card-domain">{site.label ? site.domains[0] : ""}</span>
        </div>
        <StatusBadge tone={uptime.tone || "neutral"}>{uptime.label}</StatusBadge>
      </div>
      <Sparkline data={entry.spark} />
      <dl className="ws-card-stats">
        <div>
          <dt>Visitors, 7 days</dt>
          <dd>{fmtInt(entry.visitors_7d)}</dd>
        </div>
        <div>
          <dt>Change</dt>
          <dd className={change.tone ? `ws-change ${change.tone}` : "ws-change"} title={`Against ${fmtInt(entry.visitors_prev_7d)} visitors over the same span a week earlier`}>{change.text}</dd>
        </div>
        <div>
          <dt>Live</dt>
          <dd><span className={live ? "wa-live active" : "wa-live"}><i className="wa-live-dot" aria-hidden="true" /><strong>{fmtInt(entry.live)}</strong></span></dd>
        </div>
      </dl>
    </article>
  );
}
